import React from "react";
import { Box, Text, Button as ThemeUiButton, useThemeUI } from "theme-ui";
import find from "lodash/fp/find";

import * as styles from "./ProductBreakdownSelector.styles";
import { Button, SvgOrImg, ImageCarousel, StageBar, Header } from "components";
import { ArrowLeftIcon, ArrowRightIcon } from "components/icons";
import { useTranslation } from "hooks";

export default function ProductBreakdownSelector({
  appHeaderLogo,
  products,
  selectedBreakdownProduct: { category, id: productId },

  selectProduct,
  showBreakdown,
}) {
  const { t } = useTranslation();

  const {
    theme: { isDesktop },
  } = useThemeUI();

  const imagesToRender =
    category?.familyBreakdown?.images[isDesktop ? "desktop" : "mobile"];

  const currentIndex = products.findIndex(
    (product) => product.id === productId
  );

  const changeSelectedFamily = (direction) => {
    showBreakdown(
      products[(currentIndex + products.length + direction) % products.length]
    );
  };

  return (
    <Box sx={styles.productBreakdownSelector}>
      <Box sx={styles.productDetailsHeader}>
        <Header
          goBackTo={() => showBreakdown(false)}
          appHeaderLogo={appHeaderLogo}
          showLogoOnMobile
          showSeparatorOnMobile
        />
      </Box>

      <Box sx={styles.body}>
        <Box sx={styles.detailsPanel}>
          <Box sx={styles.detailsSelector}>
            <Button
              variant="secondary"
              sx={styles.changeDetailsBtn}
              iconAfter={<ArrowLeftIcon />}
              onClick={() => changeSelectedFamily(-1)}
            />

            <Box sx={styles.detailsIndicatorsContainer}>
              <Text variant="h5">{t("Choose a family")}</Text>

              <Box sx={styles.detailsIndicators}>
                {products.map(({ id, category: { color } }) => {
                  return (
                    <ThemeUiButton
                      variant="unstyled"
                      sx={styles.detailsIndicator({
                        color,
                      })}
                      onClick={() => showBreakdown(find({ id: id })(products))}
                      key={id}
                    />
                  );
                })}
                <Box
                  sx={styles.selectedDetailIndicator({
                    color: category?.color,
                    position: `${60 * currentIndex - 5}px`,
                  })}
                ></Box>
              </Box>
            </Box>

            <Button
              variant="secondary"
              sx={styles.changeDetailsBtn}
              iconAfter={<ArrowRightIcon />}
              onClick={() => changeSelectedFamily(1)}
            />
          </Box>

          <Box sx={styles.content}>
            <SvgOrImg
              src={category?.logo.url}
              alt={category?.logo.alt}
              sx={styles.logo({ widthOverride: category?.logo.widthOverride })}
            />

            <Text as="div" variant="h3Bold" sx={styles.detailsSubText}>
              {category?.productButtonProps?.subText}
            </Text>

            <Text
              as="div"
              variant="copy1Light"
              mt="20px"
              dangerouslySetInnerHTML={{
                __html: category?.familyBreakdown?.details,
              }}
            />

            <Button
              iconAfter={<ArrowRightIcon />}
              variant="primary"
              size="large"
              isRound
              sx={styles.chooseButton}
              onClick={() => selectProduct(productId)}
            >
              {t("choose") + " " + category?.name}
            </Button>

            {category?.familyBreakdown?.additionalButton && (
              <Button
                variant="secondary"
                size="medium"
                isRound
                sx={styles.extraButton}
                onClick={() => {}}
              >
                {category?.familyBreakdown?.additionalButton.text}
              </Button>
            )}
          </Box>
        </Box>

        <Box sx={styles.imagePanelAspectRatio}>
          <Box sx={styles.imagePanel}>
            <ImageCarousel
              images={imagesToRender}
              productColor={category?.color}
              textColor={category?.familyBreakdown?.textColor}
              cornerTag={category?.familyBreakdown?.cornerTag}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={styles.stageBar}>
        <StageBar />
      </Box>
    </Box>
  );
}
